import type { Transition } from 'framer-motion'

/**
 * Shared motion language. The reveal timing used to be retyped inline in a
 * dozen files; these are the single source of truth.
 *
 * Discrete transitions stay inside 200-300ms with an ease-out curve. Anything
 * longer in this file is scroll-linked, where duration is driven by the user's
 * scroll position rather than a fixed timeline.
 */

/** Enter / reveal. Strong ease-out so it reads as intentional. */
export const easeEnter = [0.22, 1, 0.36, 1] as const

/** Movement across the screen. */
export const easeMove = [0.25, 1, 0.5, 1] as const

/** Section and card reveals. */
export const revealTransition: Transition = {
  duration: 0.5,
  ease: easeEnter,
}

/** Small discrete UI: dropdowns, tabs, toggles. */
export const uiTransition: Transition = {
  duration: 0.22,
  ease: easeEnter,
}

/** Press feedback. */
export const pressTransition: Transition = {
  duration: 0.14,
  ease: easeEnter,
}

/** The offscreen state every reveal starts from. */
export const revealFrom = {
  opacity: 0,
  y: 18,
}

export const revealTo = {
  opacity: 1,
  y: 0,
}

export const viewportOnce = {
  once: true,
  amount: 0.3,
}

/**
 * Per-item stagger delay, capped so the last item in a long list never feels
 * late. 40ms per item, never more than 240ms total.
 */
export function stagger(index: number, reduceMotion: boolean | null): number {
  if (reduceMotion) return 0
  return Math.min(index * 0.04, 0.24)
}

/**
 * Reveal props for a motion element. Returns `initial: false` under reduced
 * motion so the element renders in its finished state.
 */
export function reveal(reduceMotion: boolean | null, index = 0) {
  return {
    initial: reduceMotion ? (false as const) : revealFrom,
    whileInView: revealTo,
    viewport: viewportOnce,
    transition: {
      ...revealTransition,
      delay: stagger(index, reduceMotion),
    },
  }
}
